import type { LocationItem } from "./types";

import Location from "@homeinstead/icons/Location";
import { formatPhoneDisplay } from "@homeinstead/shared";
import { cn } from "@homeinstead/ui";
import { SquareArrowOutUpRight } from "lucide-react";
import { TrackedPhoneLink } from "~www/components/TrackedPhoneLink";

interface LocationListItemProps {
  location: LocationItem;
  isSelected: boolean;
  onSelect?: () => void;
  ref?: React.Ref<HTMLDivElement>;
}

function OfficeName({ location }: { location: LocationItem }) {
  return (
    <>
      <span className="font-bold">{location.officeName}</span>
      {location.officeSubtitle && (
        <span className="font-medium">, {location.officeSubtitle}</span>
      )}
    </>
  );
}

export function LocationListItem({
  location,
  isSelected,
  onSelect,
  ref,
}: LocationListItemProps) {
  const interactive = onSelect != null && location.coordinates != null;

  return (
    <div
      aria-pressed={interactive ? isSelected : undefined}
      className={cn(
        "flex items-start justify-between gap-2 rounded-2xl border bg-white p-4 shadow-card-soft transition-colors",
        interactive && "cursor-default",
        isSelected
          ? "border-new-beginnings-base ring-1 ring-new-beginnings-base"
          : "border-new-beginnings-tints-900",
      )}
      onClick={interactive ? () => onSelect() : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onSelect();
              }
            }
          : undefined
      }
      ref={ref}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
    >
      <div className="flex items-start gap-2">
        <Location className="mt-0.5 h-6 w-5 shrink-0 [&_path]:fill-new-beginnings-base" />

        <div className="flex flex-col text-base text-new-beginnings-base leading-7">
          {location.canonicalUrl ? (
            <a
              className="self-start underline hover:no-underline"
              href={location.canonicalUrl}
              onClick={(e) => e.stopPropagation()}
            >
              <OfficeName location={location} />
            </a>
          ) : (
            <p>
              <OfficeName location={location} />
            </p>
          )}

          {location.addressLine1 && <span>{location.addressLine1}</span>}
          {location.cityStateZip && <span>{location.cityStateZip}</span>}

          {location.phone && (
            <TrackedPhoneLink
              className="mt-3 self-start"
              phoneNumber={location.phone}
              placement="location search"
              size="inline"
              variant="plain"
            >
              {formatPhoneDisplay(location.phone)}
            </TrackedPhoneLink>
          )}
        </div>
      </div>

      {location.canonicalUrl ? (
        <a
          aria-label={`View ${location.officeName} office page`}
          className="mt-0.5 shrink-0"
          href={location.canonicalUrl}
          onClick={(e) => e.stopPropagation()}
          rel="noopener noreferrer"
          target="_blank"
        >
          <SquareArrowOutUpRight className="h-6 w-6 text-new-beginnings-base" />
        </a>
      ) : null}
    </div>
  );
}
